import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft } from 'lucide-react';
import { Address, User, PaymentType, PaymentInitResponse, CardInput } from '../../types';
import AddressList from '../AddressList';
import PaymentMethodSelector from '../payment/PaymentMethodSelector';
import CardForm from '../payment/CardForm';
import ThreeDSecureModal from '../payment/ThreeDSecureModal';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { notify } from '../../lib/toast';
import { useCart } from '../../contexts/CartContext';
import { useConfetti } from '../../hooks/useConfetti';

interface CartCheckoutModalProps {
    isOpen: boolean;
    onClose: () => void;
    items: any[]; // CartItems
    sellerId: string;
    sellerName: string;
    totalAmount: number;
    currentUser: User | null;
}

const CartCheckoutModal: React.FC<CartCheckoutModalProps> = ({
    isOpen,
    onClose,
    items,
    sellerId,
    sellerName,
    totalAmount,
    currentUser
}) => {
    const { clearCart } = useCart();
    const { celebrate } = useConfetti();

    // Steps
    const [step, setStep] = useState<'ADDRESS' | 'PAYMENT' | 'SUCCESS'>('ADDRESS');

    // Data
    const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
    const [paymentMethod, setPaymentMethod] = useState<'wallet' | 'card'>('card');

    // Loading
    const [loading, setLoading] = useState(false);

    // 3D Secure
    const [show3DS, setShow3DS] = useState(false);
    const [threeDSHtml, setThreeDSHtml] = useState<string>('');
    const [threeDSPaymentId, setThreeDSPaymentId] = useState<string>('');

    if (!isOpen || !currentUser) return null;

    const handleBack = () => {
        if (step === 'PAYMENT') setStep('ADDRESS');
        else onClose();
    };

    const handleAddressSelect = (address: Address) => {
        setSelectedAddress(address);
    };

    const handleContinueToPayment = () => {
        if (!selectedAddress) {
            notify.error('Lütfen bir teslimat adresi seçin.');
            return;
        }
        setStep('PAYMENT');
    };

    // Wallet Payment
    const handleWalletPayment = async () => {
        if (!selectedAddress) return;
        setLoading(true);

        try {
            const walletPayFn = httpsCallable(functions, 'walletPay');
            // Call Cloud Function with CART_PURCHASE type
            const result = await walletPayFn({
                type: 'CART_PURCHASE',
                sellerId: sellerId,
                shippingAddress: selectedAddress,
                listingId: 'dummy' // Not needed but satisfies strict types if any
            });

            const data = result.data as any;
            if (data.success) {
                setStep('SUCCESS');
                celebrate(); // 🎉 Confetti!
                setTimeout(() => {
                    onClose();
                    setStep('ADDRESS');
                }, 3000);
            }
        } catch (error: any) {
            console.error(error);
            notify.error(error.message || 'Ödeme sırasında bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    // Card Payment
    const handleCardPayment = async (cardData: CardInput) => {
        if (!selectedAddress) return;
        setLoading(true);

        try {
            const initPaymentFn = httpsCallable(functions, 'initPayment');
            const result = await initPaymentFn({
                amount: totalAmount,
                type: 'CART_PURCHASE',
                card: cardData,
                sellerId: sellerId,
                shippingAddress: selectedAddress,
                idempotencyKey: `cart-${sellerId}-${Date.now()}`
            });

            const response = result.data as PaymentInitResponse;

            if (response.success) {
                if (response.requires3D && response.threeDSHtml) {
                    setThreeDSHtml(response.threeDSHtml);
                    setThreeDSPaymentId(response.paymentId);
                    setShow3DS(true);
                } else {
                    setStep('SUCCESS');
                    celebrate(); // 🎉 Confetti!
                    setTimeout(() => {
                        onClose();
                        setStep('ADDRESS');
                    }, 3000);
                }
            } else {
                notify.error(response.errorMessage || 'Ödeme başarısız.');
            }
        } catch (error: any) {
            console.error(error);
            notify.error(error.message || 'Ödeme başlatılamadı.');
        } finally {
            setLoading(false);
        }
    };

    const handle3DSComplete = (success: boolean) => {
        setShow3DS(false);
        if (success) {
            setStep('SUCCESS');
            celebrate(); // 🎉 Confetti!
            setTimeout(() => {
                onClose();
                setStep('ADDRESS');
            }, 3000);
        } else {
            notify.error('3D Doğrulama başarısız veya iptal edildi.');
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pointer-events-none">
            {/* Backdrop */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
                className="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-auto"
            />

            {/* Modal */}
            <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="relative w-full max-w-lg bg-white dark:bg-gray-950 rounded-t-[2.5rem] sm:rounded-[2.5rem] shadow-2xl overflow-hidden pointer-events-auto max-h-[90vh] flex flex-col border-t sm:border border-gray-100 dark:border-gray-800"
            >
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white dark:bg-gray-950 border-b border-gray-100 dark:border-gray-800 p-6 flex items-center justify-between">
                    <button
                        onClick={handleBack}
                        disabled={step === 'SUCCESS'}
                        className="w-10 h-10 flex items-center justify-center bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all text-gray-900 dark:text-white active:scale-95 disabled:opacity-0"
                    >
                        {step === 'ADDRESS' ? <X size={20} strokeWidth={3} /> : <ChevronLeft size={20} strokeWidth={3} />}
                    </button>
                    <h2 className="font-black text-xl text-gray-950 dark:text-white uppercase tracking-tighter italic">
                        {step === 'ADDRESS' ? 'Adres Seçimi' : step === 'SUCCESS' ? 'Tebrikler' : 'Güvenli Ödeme'}
                    </h2>
                    <div className="w-10" /> {/* Spacer */}
                </div>

                {/* Content */}
                <div className="overflow-y-auto p-4 space-y-6 flex-1">
                    {step === 'ADDRESS' ? (
                        <>
                            <div className="p-6 bg-blue-500/5 dark:bg-blue-500/10 rounded-2xl border border-blue-500/10">
                                <p className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-[0.2em] mb-1.5">Sipariş Verilen Satıcı</p>
                                <p className="font-black text-gray-950 dark:text-white text-lg tracking-tight uppercase italic">{sellerName}</p>
                            </div>

                            <div className="space-y-4">
                                <p className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] ml-2">Teslimat Adresim</p>
                                <AddressList
                                    selectable
                                    selectedId={selectedAddress?.id}
                                    onSelect={handleAddressSelect}
                                />
                            </div>

                            <div className="pt-6">
                                <button
                                    onClick={handleContinueToPayment}
                                    disabled={!selectedAddress}
                                    className={`w-full py-5 rounded-2xl font-black uppercase tracking-[0.2em] shadow-2xl transition-all active:scale-95 text-sm ${selectedAddress
                                        ? 'bg-gray-950 dark:bg-white text-white dark:text-gray-950'
                                        : 'bg-gray-100 dark:bg-gray-900 text-gray-400 dark:text-gray-600 cursor-not-allowed border border-gray-200 dark:border-gray-800'
                                        }`}
                                >
                                    Ödeme Adımına Geç
                                </button>
                            </div>
                        </>
                    ) : step === 'PAYMENT' ? (
                        /* PAYMENT STEP */
                        <div className="space-y-8">
                            {/* Summary Box */}
                            <div className="p-6 bg-gray-50 dark:bg-gray-900 rounded-[2rem] border border-gray-100 dark:border-gray-800 space-y-4">
                                <div className="flex justify-between items-center text-xs font-black uppercase tracking-widest">
                                    <span className="text-gray-400 dark:text-gray-500">Teslimat Adresi</span>
                                    <span className="text-gray-950 dark:text-white line-clamp-1 max-w-[50%]">{selectedAddress?.title}</span>
                                </div>
                                <div className="flex justify-between items-center border-t border-gray-100 dark:border-gray-800 pt-4">
                                    <span className="text-gray-400 dark:text-gray-500 font-black uppercase tracking-widest text-[10px]">Toplam Tutar</span>
                                    <span className="font-black text-gray-950 dark:text-white text-2xl tracking-tighter italic">₺{totalAmount.toLocaleString('tr-TR')}</span>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <p className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] ml-2">Ödeme Yöntemi</p>
                                <PaymentMethodSelector
                                    selectedMethod={paymentMethod}
                                    onMethodSelect={setPaymentMethod}
                                    price={totalAmount}
                                    userBalance={currentUser?.balance || 0}
                                />
                            </div>

                            {paymentMethod === 'card' && (
                                <div className="bg-white dark:bg-gray-950 rounded-3xl border border-gray-100 dark:border-gray-800 p-6 shadow-sm">
                                    <CardForm
                                        onSubmit={handleCardPayment}
                                        isProcessing={loading}
                                    />
                                </div>
                            )}

                            {paymentMethod === 'wallet' && (
                                <button
                                    onClick={handleWalletPayment}
                                    disabled={loading || (currentUser?.balance || 0) < totalAmount}
                                    className={`w-full py-5 rounded-2xl font-black uppercase tracking-[0.2em] text-sm shadow-2xl transition-all active:scale-95 flex items-center justify-center gap-3 ${loading || (currentUser?.balance || 0) < totalAmount
                                        ? 'bg-gray-100 dark:bg-gray-900 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                                        : 'bg-gray-950 dark:bg-white text-white dark:text-gray-950'
                                        }`}
                                >
                                    {loading ? 'İşleniyor...' : `Bakiyem ile Öde (₺${totalAmount.toLocaleString('tr-TR')})`}
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 space-y-8 text-center">
                            <motion.div
                                className="relative w-48 h-48 bg-white dark:bg-gray-900 rounded-[3rem] shadow-xl border border-gray-100 dark:border-gray-800 flex items-center justify-center overflow-hidden"
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.5, ease: "backOut" }}
                            >
                                <motion.div
                                    initial={{ pathLength: 0, opacity: 0 }}
                                    animate={{ pathLength: 1, opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                    className="w-24 h-24"
                                >
                                    <svg viewBox="0 0 52 52" className="w-full h-full text-green-500 fill-none stroke-current" strokeWidth={6} strokeLinecap="round">
                                        <motion.path
                                            initial={{ pathLength: 0 }}
                                            animate={{ pathLength: 1 }}
                                            transition={{ duration: 0.8, delay: 0.3, ease: "easeInOut" }}
                                            d="M14.1 27.2l7.1 7.2 16.7-16.8"
                                        />
                                    </svg>
                                </motion.div>
                                <AnimatePresence>
                                    {[...Array(12)].map((_, i) => (
                                        <motion.div
                                            key={i}
                                            initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                                            animate={{
                                                x: Math.cos(i * 30 * Math.PI / 180) * 60,
                                                y: Math.sin(i * 30 * Math.PI / 180) * 60,
                                                scale: 1,
                                                opacity: 0
                                            }}
                                            transition={{ duration: 0.8, delay: 0.5 }}
                                            className="absolute w-1.5 h-1.5 bg-green-400 rounded-full"
                                        />
                                    ))}
                                </AnimatePresence>
                            </motion.div>
                            <div className="space-y-2">
                                <h3 className="text-2xl font-black text-gray-950 dark:text-white uppercase tracking-tighter italic">Siparişiniz Alındı!</h3>
                                <p className="text-gray-500 dark:text-gray-400 font-medium">Bizi tercih ettiğiniz için teşekkürler. Varlıklarınız yola çıkmak üzere.</p>
                            </div>
                            <div className="w-full max-w-xs p-4 bg-blue-500/5 dark:bg-blue-500/10 rounded-2xl border border-blue-500/10">
                                <p className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest">Sipariş No: #FCT-{Math.floor(100000 + Math.random() * 900000)}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* 3D Secure Modal */}
                <AnimatePresence>
                    {show3DS && (
                        <ThreeDSecureModal
                            htmlContent={threeDSHtml}
                            onClose={loading ? undefined : () => setShow3DS(false)}
                        // Try to prevent premature close if polling?
                        // But usually users close it manually if stuck.
                        />
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
};

export default CartCheckoutModal;
